import type * as zod from 'zod';

import * as log from '@sb/log';
import {
  ControlType,
  StreamType,
} from '@sb/messenger/auto_generated/messenger/external_control_bot_service';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import { TcServoSession } from './TcServoSession';
import Variables from './Variables';

const ns = log.namespace('ExternalControlStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class ExternalControlStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  private controlSource: string | null = null;

  private streamStarted: boolean = false;

  private clientId: string = `external-control-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  /** Live TC ServoJ control-plane session, when streaming is TC-routed. */
  private tcServoSession: TcServoSession | null = null;

  /** First TC fault reason observed mid-session; surfaced as a step failure. */
  private tcServoFaultReason: string | null = null;

  /** True once the fault path already called fail() for this play cycle, so
   * the post-substep backstop doesn't fail the step a second time. */
  private tcServoFailIssued = false;

  /** Set on the first release request; blocks a TC servo session from starting
   * after teardown has already begun. */
  private releaseRequested = false;

  /** Single shared teardown: _play's finally, _pause, and _stop can all request
   * a release concurrently — they must share one run so the lease is released
   * exactly once, and only after the TC teardown reached IDLE. */
  private releaseControlSourcePromise: Promise<void> | null = null;

  public initializeVariableState(): void {
    // Surface the session modality in the runner state so any client can read
    // the live session directly (see Variables.ts).
    this.variables = {
      controlledBy: this.args.controlledBy ?? 'api',
      streamType: this.args.streamType ?? 'pose',
    };
  }

  public async _play({ playSubSteps, fail }: StepPlayArguments): Promise<void> {
    // Step instances are replayed (a Loop substep, or pause/resume), so each
    // play cycle must start with clean teardown/fault state. The teardown
    // memoization below is per-cycle by design; if it leaked into the next
    // cycle, releaseControlSource() would no-op and leak the re-acquired
    // lease/stream, and the stale releaseRequested/fault flags would fail every
    // later play. (streamStarted/controlSource already self-reset inside
    // endExternalStream()/releaseControlSource().)
    this.releaseRequested = false;
    this.releaseControlSourcePromise = null;
    this.tcServoFaultReason = null;
    this.tcServoFailIssued = false;

    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason: 'External Control Step not supported in visualizer',
      });
    }

    // Require feature flag
    if (!getFeatureFlagSync('externalControlStep', false)) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason:
          'External Control Step requires the externalControlStep feature flag to be enabled',
      });
    }

    // Require CSM feature flag
    if (!getFeatureFlagSync('useControlSourceManager', false)) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason:
          'External Control Step requires the Control Source Manager feature flag to be enabled',
      });
    }

    try {
      // Get control source (throws if incompatible)
      const controlSource = this.getControlSource();
      // Request control source
      await this.requestControlSource(controlSource);

      // For a TC-routed servo session, enqueue the ServoJ primitive on the
      // trajectory controller BEFORE starting the external stream, so ECB only
      // begins pushing WriteServoJ targets once the primitive is RUNNING. The
      // lease was acquired just above — the primitive seeds its filter from the
      // commanded position, so the lease must be held before the enqueue.
      if (this.isTcServoModality()) {
        await this.startTcServoSession(fail);
      }

      // A stop/pause can land while startTcServoSession() awaits the
      // ClearError+Enqueue window — which resolves normally (not via throw), so
      // control falls through here. Teardown has already run the memoized
      // release and dropped the lease; starting the stream now would leak a ROS
      // bridge with no lease backing it. Bail before startExternalStream.
      if (this.releaseRequested) {
        return;
      }

      // Begin the external-control session: starts the ROS bridge for
      // rosHumble/rosJazzy; a no-op for api (the client speaks the SDK directly).
      await this.startExternalStream();

      // Play substeps if any
      if (this.substeps && this.substeps.length > 0) {
        await playSubSteps();
      }

      // Backstop for a fault that lands exactly while the substeps unwind —
      // the live-session path fails the step from onFault itself (nothing
      // interrupts the substeps, so waiting until here would never run).
      // Recovery is a client-driven restart, never an auto re-arm.
      if (this.tcServoFaultReason && !this.tcServoFailIssued) {
        throw new Error(this.tcServoFaultReason);
      }
    } catch (error) {
      log.error(ns`play`, 'Error in external control step', { error });

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason: `External Control Step failed: ${(error as Error)?.message}`,
        error: error as Error,
      });
    } finally {
      // Release control source on exit
      await this.releaseControlSource();
    }
  }

  public async _pause(_args: StepAction): Promise<void> {
    // Release control source when paused
    await this.releaseControlSource();
  }

  public async _stop(): Promise<void> {
    // Release control source when stopped
    await this.releaseControlSource();
  }

  /**
   * Get the control source this step acquires from arm-control-bot, based on the
   * configured `streamType`. Each modality maps to the source arm-control-bot tags
   * the corresponding stream with, so the lease matches exactly and no command is
   * silently dropped:
   *
   *  - pose            → COMPLIANCE_JOINT_TRAJECTORY when complianceMode is on
   *                      (pose routes through the compliance/pose/write path),
   *                      otherwise ROS_POSE — the source arm-control-bot's
   *                      /hardware/pose/write subscriber tags commands with
   *                      (PoseSubscriber::DEFAULT_SOURCE), so the lease matches.
   *  - joints          → EXTERNAL_JOINTS: joint setpoints stream on the dedicated
   *                      `stream_joints` topic, kept off the trajectory controller's
   *                      /hardware/joint_trajectory so the two never collide.
   *  - jointTrajectory → ROS_JOINT_TRAJECTORY: the scaled_joint_trajectory_controller
   *                      runs unchanged; SbotHW feeds /hardware/joint_trajectory tagged
   *                      ROS_JOINT_TRAJECTORY.
   *
   * Under `onlinePlannerStreaming`, joint-setpoint streaming is instead routed through the
   * trajectory controller's ServoJ primitive, so `joints` leases the reserved
   * `TRAJECTORY_CONTROLLER` source (the TC's output tag) rather than `EXTERNAL_JOINTS`.
   */
  private getControlSource(): string {
    const streamType = this.args.streamType ?? 'pose';

    switch (streamType) {
      case 'joints':
        return this.isOnlinePlannerStreamingActive()
          ? 'TRAJECTORY_CONTROLLER'
          : 'EXTERNAL_JOINTS';
      case 'jointTrajectory':
        return 'ROS_JOINT_TRAJECTORY';
      case 'pose':
      default:
        return getFeatureFlagSync('complianceMode', false)
          ? 'COMPLIANCE_JOINT_TRAJECTORY'
          : 'ROS_POSE';
    }
  }

  /**
   * TC-routed streaming requires ALL THREE flags: `onlinePlannerStreaming`
   * selects the ServoJ path, `useTrajectoryController` (the TC master) makes
   * the TC module and its connector exist at all, and `zmqPoseMigration`
   * selects external-control-bot's ZMQ backend — the only backend with a
   * WriteServoJ push. Missing either prerequisite would lease the arm to a
   * controller that can't drive it (no TC running, or no WriteServoJ
   * producer), so fall back to the direct path and warn.
   */
  private isOnlinePlannerStreamingActive(): boolean {
    if (!getFeatureFlagSync('onlinePlannerStreaming', false)) {
      return false;
    }

    if (!getFeatureFlagSync('useTrajectoryController', false)) {
      log.warn(
        ns`isOnlinePlannerStreamingActive`,
        'onlinePlannerStreaming is enabled but useTrajectoryController is off; using the direct joint-streaming path',
      );

      return false;
    }

    if (!getFeatureFlagSync('zmqPoseMigration', false)) {
      log.warn(
        ns`isOnlinePlannerStreamingActive`,
        'onlinePlannerStreaming is enabled but zmqPoseMigration is off; using the direct joint-streaming path',
      );

      return false;
    }

    return true;
  }

  /**
   * True when this session streams joint setpoints through the trajectory
   * controller's ServoJ primitive: the `joints` modality with TC streaming
   * active. Pose / jointTrajectory never route through the TC.
   */
  private isTcServoModality(): boolean {
    return (
      (this.args.streamType ?? 'pose') === 'joints' &&
      this.isOnlinePlannerStreamingActive()
    );
  }

  /**
   * Enqueue the ServoJ primitive and begin observing the TC. Runs after the
   * lease is acquired and before the external stream starts. The high-rate
   * WriteServoJ target stream itself is pushed by external-control-bot; the
   * step owns only the control-plane.
   */
  private async startTcServoSession(
    fail: StepPlayArguments['fail'],
  ): Promise<void> {
    // A stop/pause may already have run the (memoized) release while _play was
    // still between awaits — don't start a session nothing will tear down.
    if (this.releaseRequested) {
      throw new Error(
        'External control session ended before the TC servo session started',
      );
    }

    const connector = this.routineContext.trajectoryControllerConnector;

    if (!connector) {
      throw new Error(
        'onlinePlannerStreaming is enabled but no trajectoryControllerConnector is available',
      );
    }

    this.tcServoSession = new TcServoSession(connector, this.routineContext, {
      // Customer-tunable ServoJ settings from the start() request; omitted
      // fields fall back to the defaults inside TcServoSession.
      servoConfig: this.args.servoConfig,
      onFault: (reason) => {
        this.tcServoFaultReason ??= reason;

        log.error(ns`tcServoFault`, 'TC servo session faulted', {
          reason,
          clientId: this.clientId,
        });

        // Fail the routine from here: during a live session _play is parked
        // inside the substeps and nothing else interrupts them, so a session
        // that dies (stream timeout, TC fault) would otherwise leave the
        // routine "running" with the lease held while the data plane is dead.
        // Failing stops the running steps, which drives the ordered teardown
        // (_stop → releaseControlSource). Never auto-recover — the client
        // re-establishes by starting a new session. Skip once teardown began:
        // the session is ending anyway and the release path owns the lease.
        if (this.releaseRequested || this.tcServoFailIssued) {
          return;
        }

        this.tcServoFailIssued = true;

        fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'ExternalControl',
          },
          failureReason: `External Control Step failed: ${reason}`,
        }).catch((error) => {
          log.error(ns`tcServoFault`, 'Failed to fail the routine', { error });
        });
      },
    });

    await this.tcServoSession.start();
  }

  /**
   * Stop the TC servo primitive (Stop → wait IDLE → ClearQueue) if one is
   * running. Idempotent; safe on every teardown path.
   */
  private async stopTcServoSession(): Promise<void> {
    const session = this.tcServoSession;

    if (!session) {
      return;
    }

    // stop() is idempotent and waits out an in-flight start(), so a session
    // that was still enqueueing is stopped here rather than orphaned. Only
    // clear the field once teardown actually finished.
    await session.stop();
    this.tcServoSession = null;
  }

  /**
   * Request control source
   */
  private async requestControlSource(source: string): Promise<void> {
    this.controlSource = source;

    log.info(ns`requestControlSource`, 'Requesting control source', {
      source,
      clientId: this.clientId,
    });

    const result =
      await this.routineContext.armControlBotConnector?.SetControlSource({
        value: source,
        clientId: this.clientId,
      });

    if (result?.error) {
      throw new Error(
        `Failed to acquire control source: ${result.error.message}`,
      );
    }
  }

  /**
   * Release control source using the same logic as teleop step
   * Matches stopAllBotsInternal() from libs/sb-robot-api/src/controllers/teleop/teleop.ts
   *
   * Reentrant-safe: concurrent callers share one teardown run.
   */
  private releaseControlSource(): Promise<void> {
    this.releaseRequested = true;
    this.releaseControlSourcePromise ??= this.doReleaseControlSource();

    return this.releaseControlSourcePromise;
  }

  private async doReleaseControlSource(): Promise<void> {
    // Each stage is isolated: this promise is memoized and shared by _pause,
    // _stop, and _play's finally, so a rejection would (a) skip the remaining
    // stages — leaving the ServoJ primitive running and the lease held with
    // the external client still able to drive the arm — and (b) never be
    // retried, since the memoization only resets on the next play cycle. The
    // pause path also invokes step.pause() without awaiting it, so a rejection
    // here would surface as an unhandled rejection and take botman down.

    // End the external-control session first (stops the ROS bridge, if any).
    try {
      await this.endExternalStream();
    } catch (error) {
      log.error(
        ns`releaseControlSource`,
        'Failed to end the external stream; continuing teardown',
        { error, clientId: this.clientId },
      );
    }

    // Then decelerate the TC servo primitive to IDLE and drop the queue BEFORE
    // releasing the lease — releasing mid-STOPPING forces a spurious TC error.
    try {
      await this.stopTcServoSession();
    } catch (error) {
      log.error(
        ns`releaseControlSource`,
        'Failed to stop the TC servo session; continuing teardown',
        { error, clientId: this.clientId },
      );
    }

    if (!this.controlSource) {
      return;
    }

    const source = this.controlSource;

    log.info(ns`releaseControlSource`, 'Releasing control source', {
      source,
      clientId: this.clientId,
    });

    try {
      await this.routineContext.armControlBotConnector?.ReleaseControlFromSource(
        {
          value: source,
          clientId: this.clientId,
        },
      );

      this.controlSource = null;
    } catch (error) {
      log.error(ns`releaseControlSource`, 'Failed to release control source', {
        error,
        source,
        clientId: this.clientId,
      });
    }
  }

  /** Map the user-facing `controlledBy` arg to the messenger ControlType. */
  private controlType(): ControlType {
    switch (this.args.controlledBy ?? 'api') {
      case 'rosHumble':
        return ControlType.CONTROL_TYPE_ROS_HUMBLE;
      case 'rosJazzy':
        return ControlType.CONTROL_TYPE_ROS_JAZZY;
      case 'api':
      default:
        return ControlType.CONTROL_TYPE_API;
    }
  }

  /** Map the user-facing `streamType` arg to the messenger StreamType (logged
   * only by external-control-bot — the lease is what actually gates commands). */
  private protoStreamType(): StreamType {
    switch (this.args.streamType ?? 'pose') {
      case 'joints':
        return StreamType.STREAM_TYPE_JOINTS;
      case 'jointTrajectory':
        return StreamType.STREAM_TYPE_TRAJECTORY;
      case 'pose':
      default:
        return StreamType.STREAM_TYPE_POSE;
    }
  }

  /**
   * Tell external-control-bot to begin the session. For rosHumble/rosJazzy this
   * spins up the ros-distro-bridge with the matching distro; for api it is a
   * no-op (the client speaks the SDK protocol directly).
   */
  private async startExternalStream(): Promise<void> {
    // Set the flag before the call so teardown always ends the session even if
    // the request races a failure after the bridge has started.
    this.streamStarted = true;

    log.info(ns`startExternalStream`, 'Starting external stream', {
      controlledBy: this.args.controlledBy ?? 'api',
      streamType: this.args.streamType ?? 'pose',
      clientId: this.clientId,
    });

    await this.routineContext.externalControlBotConnector?.StartExternalStream({
      controlType: this.controlType(),
      streamType: this.protoStreamType(),
    });
  }

  /** End the external-control session (stops the ROS bridge if one was started). */
  private async endExternalStream(): Promise<void> {
    if (!this.streamStarted) {
      return;
    }

    this.streamStarted = false;

    log.info(ns`endExternalStream`, 'Ending external stream', {
      clientId: this.clientId,
    });

    await this.routineContext.externalControlBotConnector?.EndExternalStream(
      {},
    );
  }
}
